/**
 * DataExport component — lets users request a CSV of their transaction history.
 *
 * Reads the push subscription ID from localStorage and posts an export request
 * to the backend. Progress updates and the download link are delivered via
 * push notifications (which the user has already enabled to see this component).
 */

import { useState } from 'react';
import { format, subYears } from 'date-fns';
import { DatePicker } from './DatePicker';
import { requestExport } from '@/services/export';
import { getStoredSubscriptionId } from '@/services/webpush';
import type { CsvConfig } from '@/types/export';

interface DataExportProps {
  isSubscribed: boolean;
}

export function DataExport({ isSubscribed }: DataExportProps): JSX.Element | null {
  const today = format(new Date(), 'yyyy-MM-dd');
  const oneYearAgo = format(subYears(new Date(), 1), 'yyyy-MM-dd');

  const [address, setAddress] = useState('');
  const [startDate, setStartDate] = useState(oneYearAgo);
  const [endDate, setEndDate] = useState(today);
  const [combineStakingRewards, setCombineStakingRewards] = useState(false);
  const [includeFailedTx, setIncludeFailedTx] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  if (!isSubscribed) {
    return null;
  }

  const subscriptionId = getStoredSubscriptionId();

  function validateAddress(addr: string): string | null {
    const trimmed = addr.trim();
    if (!trimmed) return 'Address is required.';
    if (!trimmed.startsWith('gravity1')) return "Address must start with 'gravity1'.";
    if (trimmed.length < 39) return 'Address is too short. Check the address format.';
    return null;
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setStatusMessage(null);

    const addrError = validateAddress(address);
    if (addrError !== null) {
      setStatusMessage({ type: 'error', text: addrError });
      return;
    }

    if (!startDate || !endDate) {
      setStatusMessage({ type: 'error', text: 'Please select both start and end dates.' });
      return;
    }

    if (startDate >= endDate) {
      setStatusMessage({ type: 'error', text: 'Start date must be before end date.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const csvConfig: CsvConfig = {
        combine_staking_rewards: combineStakingRewards,
        include_failed: includeFailedTx,
      };
      const response = await requestExport(subscriptionId!, address.trim(), startDate, endDate, csvConfig);
      setStatusMessage({
        type: 'success',
        text: `${response.message} Job ID: ${response.job_id.slice(0, 8)}…`,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
            'Export request failed. Please try again.';
      setStatusMessage({ type: 'error', text: message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="mt-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold text-gray-900">Transaction History Export</h2>
      <p className="mt-1 text-sm text-gray-600">
        Export a CSV of your Gravity Bridge transaction history for a date range. You'll receive
        push notifications with progress updates and a download link when the export is ready.
      </p>

      <form onSubmit={(e) => { void handleSubmit(e); }} className="mt-6 space-y-4">
        {/* Address input */}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700" htmlFor="export-address">
            Gravity Address
          </label>
          <input
            id="export-address"
            type="text"
            value={address}
            onChange={(e) => { setAddress(e.target.value); }}
            placeholder="gravity1..."
            disabled={isSubmitting}
            spellCheck={false}
            className={[
              'w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2',
              'text-sm text-white shadow-sm placeholder:text-gray-500',
              'focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
              isSubmitting ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          />
        </div>

        {/* Staking rewards mode toggle */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">Staking Rewards Format</span>
          <div className="inline-flex rounded-md border border-gray-600" role="group">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => { setCombineStakingRewards(false); }}
              className={[
                'flex-1 rounded-l-md px-3 py-2 text-sm font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500',
                !combineStakingRewards
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
                isSubmitting ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              Per Validator
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => { setCombineStakingRewards(true); }}
              className={[
                'flex-1 rounded-r-md px-3 py-2 text-sm font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500',
                combineStakingRewards
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
                isSubmitting ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              Combined
            </button>
          </div>
          <p className="text-xs text-gray-400">
            {combineStakingRewards
              ? 'One row per token per claim transaction, totalling rewards across all validators.'
              : 'One row per validator per claim transaction, showing each source separately.'}
          </p>
        </div>

        {/* Failed transactions toggle */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-700">Failed Transactions</span>
          <div className="inline-flex rounded-md border border-gray-600" role="group">
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => { setIncludeFailedTx(false); }}
              className={[
                'flex-1 rounded-l-md px-3 py-2 text-sm font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500',
                !includeFailedTx
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
                isSubmitting ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              Exclude
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => { setIncludeFailedTx(true); }}
              className={[
                'flex-1 rounded-r-md px-3 py-2 text-sm font-medium transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500',
                includeFailedTx
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700',
                isSubmitting ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              Include
            </button>
          </div>
          <p className="text-xs text-gray-400">
            {includeFailedTx
              ? 'Includes failed transactions. The Return Code column shows the error code (0 = success).'
              : 'Only successful transactions (return code 0) are included in the export.'}
          </p>
        </div>

        {/* Date range */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            max={endDate || today}
            disabled={isSubmitting}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            min={startDate}
            max={today}
            disabled={isSubmitting}
          />
        </div>

        {/* Status message */}
        {statusMessage !== null && (
          <div
            className={[
              'rounded-md px-4 py-3 text-sm',
              statusMessage.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200',
            ].join(' ')}
          >
            {statusMessage.text}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className={[
            'w-full rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm',
            'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2',
            isSubmitting
              ? 'cursor-not-allowed bg-indigo-300'
              : 'bg-indigo-600 hover:bg-indigo-700',
          ].join(' ')}
        >
          {isSubmitting ? 'Requesting Export…' : 'Request Export'}
        </button>
      </form>

      <p className="mt-4 text-xs text-gray-400">
        Exports are available for download for 7 days. Large date ranges may take several minutes
        to process.
      </p>
    </div>
  );
}
